import { workTypeUIMap } from 'components/modules/ProjectManagement/Dropdowns/TaskTypeDropdown';
import React from 'react';
import { Task } from 'types/project/tasks';
import { IoIosArrowRoundUp } from 'react-icons/io';

const PRIORITY_ARROW_TEXT_SIZES = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
} as const;

// Standardized priority icon — this is the icon/count/color mapping used
// across the Project Work Item List (Kanban, task table, task detail via
// PriorityDropdown) and shared here so My Tasks renders identically:
// Low = 3 arrows, Medium = 2, High = 1 (fewer arrows = more urgent).
export const PriorityArrows: React.FC<{ priority: string; size?: keyof typeof PRIORITY_ARROW_TEXT_SIZES; className?: string }> = ({ priority, size = 'md', className = '' }) => {
  const config = {
    low: { arrowCount: 3, colorHex: '#22C55E' },
    medium: { arrowCount: 2, colorHex: '#FBBF24' },
    high: { arrowCount: 1, colorHex: '#EF4444' },
  }[priority.toLowerCase()] || { arrowCount: 2, colorHex: '#FBBF24' };

  return (
    <div className={`flex items-center ${PRIORITY_ARROW_TEXT_SIZES[size]} ${className}`} style={{ color: config.colorHex }}>
      {Array.from({ length: config.arrowCount }, (_, i) => (
        <IoIosArrowRoundUp key={i} />
      ))}
    </div>
  );
};

// Project colored bullet dot helper matching screenshot
export const getProjectDotColor = (projectUuid: string) => {
  const colorMap: Record<string, string> = {
    'proj-worklap': 'bg-[#8B5CF6]',   // Violet/Purple
    'proj-workfall': 'bg-[#F97316]',  // Orange
    'proj-headless': 'bg-[#10B981]',  // Green
  };
  return colorMap[projectUuid] || 'bg-[#8B5CF6]';
};

// Status pill styles helper matching screenshot
export const getStatusStyles = (statusCategory: string) => {
  switch (statusCategory) {
    case 'IN_PROGRESS':
      return 'bg-[#5B8DFF1F] text-[#5B8DFF]';
    case 'DONE':
      return 'bg-[#2ECC8F1F] text-[#2ECC8F]';
    case 'TODO':
    default:
      return 'bg-[#1f242f] text-[#9ca3af]';
  }
};

// Helper date comparisons for tabs
export const isOverdue = (task: Task) => {
  const dueDateStr = task.dueDate || task.workItemDueDate;
  if (!dueDateStr) return false;
  if (task.workStatusCategory === 'DONE') return false;
  const dueDate = new Date(dueDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

export const isDueToday = (task: Task) => {
  const dueDateStr = task.dueDate || task.workItemDueDate;
  if (!dueDateStr) return false;
  if (task.workStatusCategory === 'DONE') return false;
  const dueDate = new Date(dueDateStr);
  const today = new Date();
  return (
    dueDate.getDate() === today.getDate() &&
    dueDate.getMonth() === today.getMonth() &&
    dueDate.getFullYear() === today.getFullYear()
  );
};

export const isDueThisWeek = (task: Task) => {
  const dueDateStr = task.dueDate || task.workItemDueDate;
  if (!dueDateStr) return false;
  if (task.workStatusCategory === 'DONE') return false;
  const dueDate = new Date(dueDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // End of current week (Sunday)
  const diff = today.getDate() - today.getDay() + (today.getDay() === 0 ? 0 : 7);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(diff);
  endOfWeek.setHours(23, 59, 59, 999);
  
  return dueDate >= today && dueDate <= endOfWeek;
};

// Custom Story Icon with horizontal lines removed, localized to MyTasks module
const customStoryIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Story">
    <path
      d="M14.341 3.579C13.994 3.106 13.51 2.552 12.979 2.021C12.448 1.49 11.894 1.006 11.421 0.659C10.615 0.068 10.224 0 10 0H2.25C1.561 0 1 0.561 1 1.25V14.75C1 15.439 1.561 16 2.25 16H13.75C14.439 16 15 15.439 15 14.75V5C15 4.776 14.932 4.385 14.341 3.579ZM12.271 2.729C12.751 3.209 13.127 3.641 13.405 4H10.999V1.595C11.358 1.873 11.792 2.249 12.271 2.729ZM14 14.75C14 14.886 13.886 15 13.75 15H2.25C2.18394 14.9992 2.1208 14.9726 2.07408 14.9259C2.02737 14.8792 2.00078 14.8161 2 14.75V1.25C2 1.115 2.115 1 2.25 1H10V4.5C10 4.63261 10.0527 4.75979 10.1464 4.85355C10.2402 4.94732 10.3674 5 10.5 5H14V14.75Z"
      fill="#2DD4BF"
    />
  </svg>
);

export const myTasksWorkTypeUIMap = {
  ...workTypeUIMap,
  STORY: {
    ...workTypeUIMap.STORY,
    icon: customStoryIcon,
  },
};

export const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};